import React, { useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { Text } from '@atlaskit/primitives';
import { type InputActionMeta, type OptionType } from '@atlaskit/select';
import { Flex } from '@atlassian/eoc-flex';

import { GenericSelect } from '../../common/ui/generic-select';
import { Footer } from '../../common/ui/generic-select/footer';
import { StyledValue } from '../../common/ui/styled';
import { type SelectProps } from '../../common/ui/types';
import { cleanValue } from '../../common/utils';

import messages from './messages';

// eoc-popup-select
export const FilterSelect = function <
	Option extends OptionType = OptionType,
	IsMulti extends boolean = true,
>({
	onChange,
	onClear,
	onFullList,
	onRetry,
	onShowMore,
	onInputChange,
	options,
	value: selectValue,
	label,
	isError,
	isLoading,
	isPaginationLoading,
	totalCount = null,
	searchThreshold,
	onClose,
	formatOptionLabel,
	isFullList = false,
	target,
	onMenuOpen,
	defaultIsOpen,
	closeMenuOnSelect,
	filterOption,
	'aria-label': ariaLabel,
	ariaLiveMessages: ariaLiveMessages,
	hasNextPage,
	components,
	placeholder,
	shouldRenderToParent,
	onKeyDown,
	isMulti,
}: SelectProps<Option, IsMulti>) {
	const { formatMessage } = useIntl();
	const ref = useRef(null);

	const itemCount = useMemo(() => {
		if (options) {
			return options.flatMap((option) => option.options).length;
		}

		return 0;
	}, [options]);

	const handleInputChange = (newValue: string, meta: InputActionMeta) => {
		if (onInputChange) {
			onInputChange(newValue, meta);
		}
	};

	const value = cleanValue(selectValue);

	const hasValue = value?.length > 0;

	const defaultTarget: SelectProps['target'] = ({ isOpen, ...triggerProps }) => (
		<Button iconAfter={ChevronDownIcon} isSelected={isOpen || hasValue} {...triggerProps}>
			{hasValue ? (
				<Flex alignItems="center" columnGap={0.5}>
					<Flex>
						<Text weight="bold">{label}:</Text>
						<StyledValue>{value[0].label}</StyledValue>
					</Flex>
					{value.length > 1 && <Badge>+{value.length - 1}</Badge>}
				</Flex>
			) : (
				label
			)}
		</Button>
	);

	return (
		<GenericSelect<Option, IsMulti>
			ref={ref}
			isMulti={isMulti}
			onKeyDown={onKeyDown}
			searchThreshold={searchThreshold}
			options={options}
			onChange={onChange}
			value={value}
			onInputChange={handleInputChange}
			placeholder={placeholder || formatMessage(messages.placeholder)}
			target={target ?? defaultTarget}
			isError={isError}
			onRetry={onRetry}
			onShowMore={onShowMore}
			isLoading={isLoading}
			isPaginationLoading={isPaginationLoading}
			formatOptionLabel={formatOptionLabel}
			aria-label={ariaLabel}
			ariaLiveMessages={ariaLiveMessages}
			footer={
				<Footer
					isFullList={isFullList}
					hasValue={hasValue}
					itemCount={itemCount}
					onClear={onClear}
					onFullList={onFullList}
					totalCount={totalCount}
					isLoading={isLoading}
					searchThreshold={searchThreshold}
				/>
			}
			hasNextPage={hasNextPage}
			onClose={onClose}
			onMenuOpen={onMenuOpen}
			defaultIsOpen={defaultIsOpen}
			closeMenuOnSelect={closeMenuOnSelect}
			filterOption={filterOption}
			shouldRenderToParent={shouldRenderToParent}
			components={components}
		/>
	);
};
